import type {
  AcademicStructure,
  BatchJoinLink,
  BatchStaffMember,
  BatchStaffRole,
  BatchStudent,
  BatchWithPath,
  JoinOutcome,
  JoinPreview,
  MyEnrollment,
  OrgUnit,
  OrgUnitType,
  SetupStatus,
  StaffBatchAssignment,
} from '@welynk/types';
import { get, post, patch, put, del } from './http';
import { API_ENDPOINTS } from '../api-endpoints';
import type { PaginatedResponse } from '../api-types';

// ── College structure ───────────────────────────────────────────────────────

export interface CourseInput {
  name: string;
  code?: string | null;
  degreeType: string;
  durationYears: number;
  totalSeats?: number | null;
}

export interface BatchInput {
  name: string;
  startYear: number;
  endYear: number;
  currentSemester?: number;
  totalSeats?: number | null;
}

export class AcademicApiService {
  static structure(orgId: string) {
    return get<AcademicStructure>(API_ENDPOINTS.ACADEMIC.STRUCTURE(orgId));
  }
  static createOrgUnit(
    orgId: string,
    data: { type: OrgUnitType; name: string; code?: string | null; parentId?: string | null }
  ) {
    return post<OrgUnit>(API_ENDPOINTS.ACADEMIC.ORG_UNITS(orgId), data);
  }
  static updateOrgUnit(
    orgId: string,
    unitId: string,
    data: Partial<{ name: string; code: string | null; parentId: string | null }>
  ) {
    return patch<OrgUnit>(API_ENDPOINTS.ACADEMIC.ORG_UNIT(orgId, unitId), data);
  }
  static deleteOrgUnit(orgId: string, unitId: string) {
    return del<{ deleted: true }>(API_ENDPOINTS.ACADEMIC.ORG_UNIT(orgId, unitId));
  }
  static setDepartmentUnit(orgId: string, deptId: string, orgUnitId: string | null) {
    return put(API_ENDPOINTS.ACADEMIC.DEPARTMENT_UNIT(orgId, deptId), { orgUnitId });
  }
  static createCourse(orgId: string, deptId: string, data: CourseInput) {
    return post<{ id: string; name: string }>(API_ENDPOINTS.ACADEMIC.COURSES(orgId, deptId), data);
  }
  static updateCourse(orgId: string, courseId: string, data: Partial<CourseInput>) {
    return patch(API_ENDPOINTS.ACADEMIC.COURSE(orgId, courseId), data);
  }
  static deleteCourse(orgId: string, courseId: string) {
    return del(API_ENDPOINTS.ACADEMIC.COURSE(orgId, courseId));
  }
  static createBatch(orgId: string, courseId: string, data: BatchInput) {
    return post(API_ENDPOINTS.ACADEMIC.COURSE_BATCHES(orgId, courseId), data);
  }
  static updateBatch(orgId: string, batchId: string, data: Partial<BatchInput>) {
    return patch(API_ENDPOINTS.ACADEMIC.BATCH(orgId, batchId), data);
  }
  /** Re-parent a batch onto another program — how it changes department or campus. */
  static moveBatch(orgId: string, batchId: string, courseId: string) {
    return post(`${API_ENDPOINTS.ACADEMIC.BATCH(orgId, batchId)}/move`, { courseId });
  }
  static deleteBatch(orgId: string, batchId: string) {
    return del(API_ENDPOINTS.ACADEMIC.BATCH(orgId, batchId));
  }
  /** Close a batch at the end of its course; its students become alumni. */
  static graduateBatch(orgId: string, batchId: string) {
    return post<{ graduated: number }>(`${API_ENDPOINTS.ACADEMIC.BATCH(orgId, batchId)}/graduate`);
  }
  static reopenBatch(orgId: string, batchId: string) {
    return post<{ reopened: number }>(`${API_ENDPOINTS.ACADEMIC.BATCH(orgId, batchId)}/reopen`);
  }
  /** Whether batch leaderboards show the signed-in student's name to classmates. */
  static setMyLeaderboardVisibility(orgId: string, hideFromDirectory: boolean) {
    return patch(API_ENDPOINTS.ACADEMIC.MY_VISIBILITY(orgId), { hideFromDirectory });
  }
  static batches(orgId: string) {
    return get<BatchWithPath[]>(API_ENDPOINTS.ACADEMIC.BATCHES(orgId));
  }
  static batchStudents(
    orgId: string,
    batchId: string,
    params: { search?: string; page?: number; pageSize?: number; includePending?: boolean } = {}
  ) {
    return get<PaginatedResponse<BatchStudent>>(
      API_ENDPOINTS.ACADEMIC.BATCH_STUDENTS(orgId, batchId),
      {
        ...params,
        includePending: params.includePending ? 'true' : undefined,
      }
    );
  }
  static enrollStudents(
    orgId: string,
    batchId: string,
    students: Array<{ userId: string; rollNumber?: string | null }>
  ) {
    return post<{ enrolled: number; skipped: Array<{ userId: string; reason: string }> }>(
      API_ENDPOINTS.ACADEMIC.BATCH_STUDENTS(orgId, batchId),
      { students }
    );
  }
  static updateEnrollment(
    orgId: string,
    batchId: string,
    userId: string,
    data: { rollNumber: string | null }
  ) {
    return patch(API_ENDPOINTS.ACADEMIC.BATCH_STUDENT(orgId, batchId, userId), data);
  }
  static removeStudent(orgId: string, batchId: string, userId: string) {
    return del(API_ENDPOINTS.ACADEMIC.BATCH_STUDENT(orgId, batchId, userId));
  }
  static staffAssignments(orgId: string) {
    return get<StaffBatchAssignment[]>(API_ENDPOINTS.ACADEMIC.STAFF_ASSIGNMENTS(orgId));
  }
  static batchStaff(orgId: string, batchId: string) {
    return get<BatchStaffMember[]>(API_ENDPOINTS.ACADEMIC.BATCH_STAFF(orgId, batchId));
  }
  static assignStaff(orgId: string, batchId: string, userId: string, role: BatchStaffRole) {
    return post(API_ENDPOINTS.ACADEMIC.BATCH_STAFF(orgId, batchId), { userId, role });
  }
  static unassignStaff(orgId: string, batchId: string, userId: string) {
    return del(API_ENDPOINTS.ACADEMIC.BATCH_STAFF_MEMBER(orgId, batchId, userId));
  }
  static joinLinks(orgId: string, batchId: string) {
    return get<BatchJoinLink[]>(API_ENDPOINTS.ACADEMIC.JOIN_LINKS(orgId, batchId));
  }
  static createJoinLink(
    orgId: string,
    batchId: string,
    data: { expiresInDays?: number | null; maxUses?: number | null }
  ) {
    return post<BatchJoinLink>(API_ENDPOINTS.ACADEMIC.JOIN_LINKS(orgId, batchId), data);
  }
  static revokeJoinLink(orgId: string, linkId: string) {
    return del(API_ENDPOINTS.ACADEMIC.JOIN_LINK(orgId, linkId));
  }
  static previewJoin(code: string) {
    return get<JoinPreview>(API_ENDPOINTS.ACADEMIC.JOIN(code));
  }
  static join(code: string) {
    return post<JoinOutcome>(API_ENDPOINTS.ACADEMIC.JOIN(code));
  }
  /** The signed-in student's own batches in a college. */
  static myEnrollments(orgId: string) {
    return get<MyEnrollment[]>(API_ENDPOINTS.ACADEMIC.MY_ENROLLMENTS(orgId));
  }
  static setupStatus(orgId: string) {
    return get<SetupStatus>(API_ENDPOINTS.ACADEMIC.SETUP(orgId));
  }
  static completeSetup(orgId: string) {
    return post<SetupStatus>(API_ENDPOINTS.ACADEMIC.SETUP_COMPLETE(orgId));
  }
}
